import { computed, onMounted } from 'vue'
import useGet from '@/core/composables/api/useGet'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { useOrganizationStore } from '@/stores/organization.store'
import { Role } from '@/modules/organization/models/organization-role'

export interface OrganizationCapabilitySettings {
  readonly memberCanGive: boolean
  readonly memberCanInitiate: boolean
}

type SettingsRequest = Pick<
  ReturnType<typeof useGet<OrganizationCapabilitySettings>>,
  'data' | 'status' | 'load'
> & {
  started: boolean
}

const requests = new Map<string, SettingsRequest>()

export const useOrganizationCapabilitySettings = () => {
  const organizationStore = useOrganizationStore()
  const { data, status, load } = useGet<OrganizationCapabilitySettings>('organization/settings')

  let request = requests.get(organizationStore.currentOrganizationId)
  if (!request) {
    request = { data, status, load, started: false }
    requests.set(organizationStore.currentOrganizationId, request)
  }

  const sharedRequest = request

  onMounted(() => {
    if (sharedRequest.started) return
    sharedRequest.started = true
    void sharedRequest.load()
  })

  const settings = computed(() => {
    if (sharedRequest.status.value !== ApiRequestStatus.Success) return undefined
    return sharedRequest.data.value
  })

  return { settings }
}

export const roleDescriptionKey = (
  role: Role,
  settings: OrganizationCapabilitySettings | undefined,
): string => {
  if (role !== Role.MEMBER || !settings) {
    return `organization.roleDescription.${role}`
  }

  if (settings.memberCanGive && settings.memberCanInitiate) {
    return 'organization.memberCapabilities.canGiveAndInitiate'
  }
  if (settings.memberCanGive) {
    return 'organization.memberCapabilities.canGive'
  }
  if (settings.memberCanInitiate) {
    return 'organization.memberCapabilities.canInitiate'
  }
  return 'organization.memberCapabilities.receiveOnly'
}
